import { Menu, Moon, Search, Sun, X } from "lucide-react";
import { useEffect, useState } from "react";
import { NavLink, Outlet } from "react-router-dom";
import { clsx } from "clsx";
import { navGroups } from "../nav";
import { DocsCommandPalette } from "./DocsCommandPalette";

export function DocsShell() {
    const [open, setOpen] = useState(false);
    const [paletteOpen, setPaletteOpen] = useState(false);
    const [dark, setDark] = useState(() => document.documentElement.classList.contains("dark"));

    useEffect(() => {
        document.documentElement.classList.toggle("dark", dark);
    }, [dark]);

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
                e.preventDefault();
                setPaletteOpen((v) => !v);
            }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, []);

    return (
        <div className="min-h-screen bg-neutral-light dark:bg-neutral-ink">
            <DocsCommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} />

            <header className="sticky top-0 z-sticky flex h-14 items-center justify-between gap-2 border-b border-neutral-border bg-white/90 px-4 backdrop-blur dark:border-white/10 dark:bg-neutral-navy/90 lg:hidden">
                <button
                    type="button"
                    className="rounded-mz-md p-2 text-neutral-dark hover:bg-neutral-light dark:text-white dark:hover:bg-white/10"
                    aria-label={open ? "Close menu" : "Open menu"}
                    onClick={() => setOpen((v) => !v)}
                >
                    {open ? <X size={20} /> : <Menu size={20} />}
                </button>
                <span className="font-display text-sm font-bold text-primary dark:text-gold-light">
                    Maeiz DS
                </span>
                <div className="flex items-center gap-1">
                    <button
                        type="button"
                        className="rounded-mz-md p-2 text-neutral-secondary hover:bg-neutral-light dark:hover:bg-white/10"
                        aria-label="Open command palette"
                        onClick={() => setPaletteOpen(true)}
                    >
                        <Search size={18} className="text-primary dark:text-gold-light" />
                    </button>
                    <ThemeToggle dark={dark} onToggle={() => setDark((d) => !d)} />
                </div>
            </header>

            <div className="lg:flex">
                <aside
                    className={clsx(
                        "fixed inset-y-0 start-0 z-overlay w-[min(100%,280px)] overflow-y-auto border-e border-neutral-border bg-white p-4 transition-transform dark:border-white/10 dark:bg-neutral-navy lg:sticky lg:top-0 lg:z-auto lg:h-screen lg:w-[260px] lg:translate-x-0 lg:shrink-0",
                        open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
                    )}
                >
                    <div className="mb-4 hidden items-start justify-between gap-2 lg:flex">
                        <div>
                            <p className="font-display text-lg font-extrabold text-primary dark:text-gold-light">
                                Maeiz
                            </p>
                            <p className="text-xs text-neutral-secondary">
                                Workspace Design System
                            </p>
                        </div>
                        <ThemeToggle dark={dark} onToggle={() => setDark((d) => !d)} />
                    </div>

                    <button
                        type="button"
                        onClick={() => setPaletteOpen(true)}
                        className="mb-5 hidden w-full items-center gap-2 rounded-xl border border-neutral-border bg-neutral-light/80 px-3 py-2 text-left text-xs text-neutral-secondary hover:border-primary/40 dark:border-white/12 dark:bg-white/[0.04] dark:hover:border-gold/40 lg:flex"
                    >
                        <Search size={14} className="text-primary dark:text-gold-light" />
                        <span className="flex-1">Search docs…</span>
                        <kbd className="rounded border border-neutral-border px-1 font-mono text-[10px] dark:border-white/15">
                            ⌘K
                        </kbd>
                    </button>

                    <nav className="space-y-6" aria-label="Design system">
                        {navGroups.map((group) => (
                            <div key={group.title}>
                                <p className="mb-2 px-2 text-[0.6875rem] font-semibold uppercase tracking-[0.08em] text-neutral-secondary">
                                    {group.title}
                                </p>
                                <ul className="space-y-0.5">
                                    {group.items.map((item) => (
                                        <li key={item.to}>
                                            <NavLink
                                                to={item.to}
                                                end={item.to === "/"}
                                                onClick={() => setOpen(false)}
                                                className={({ isActive }) =>
                                                    clsx(
                                                        "block rounded-mz-md px-2 py-1.5 text-sm transition-colors",
                                                        isActive
                                                            ? "bg-primary/10 font-semibold text-primary dark:bg-gold/15 dark:text-gold-light"
                                                            : "text-neutral-dark hover:bg-neutral-light dark:text-slate-200 dark:hover:bg-white/5"
                                                    )
                                                }
                                            >
                                                {item.label}
                                            </NavLink>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </nav>
                </aside>

                {open ? (
                    <button
                        type="button"
                        className="fixed inset-0 z-overlay bg-black/40 lg:hidden"
                        aria-label="Close navigation overlay"
                        onClick={() => setOpen(false)}
                    />
                ) : null}

                <main className="min-w-0 flex-1">
                    <Outlet />
                </main>
            </div>
        </div>
    );
}

function ThemeToggle({ dark, onToggle }: { dark: boolean; onToggle: () => void }) {
    return (
        <button
            type="button"
            onClick={onToggle}
            className="rounded-mz-md p-2 text-neutral-secondary hover:bg-neutral-light hover:text-neutral-dark dark:hover:bg-white/10 dark:hover:text-white"
            aria-label={dark ? "Switch to light mode" : "Switch to dark mode"}
        >
            {dark ? <Sun size={18} /> : <Moon size={18} />}
        </button>
    );
}
